import type { Dungeon, Expansion } from '../data/dungeons';
import { ALL_DUNGEONS } from '../data/dungeons';
import type { CharacterSummary } from '../types';

/** Default level slack outside the dungeon's suggested band. */
export const DEFAULT_SLACK = 2;

export type Eligibility = 'green' | 'yellow' | 'heroic' | 'out';

/**
 * Classify a character's level against a dungeon.
 *  - heroic  : level >= heroicLevel (only for dungeons that have heroic mode)
 *  - green   : in the suggested band [min..max]
 *  - yellow  : within `slack` levels outside the band
 *  - out     : too low or too high
 *
 * Heroic takes priority when applicable (e.g. a level-80 vs Hellfire Ramparts
 * is reported as 'heroic' rather than 'out', because heroic is the relevant
 * way to run that dungeon at 80).
 */
export function dungeonStatus(level: number, d: Dungeon, slack = DEFAULT_SLACK): Eligibility {
  if (d.heroicLevel !== null && level >= d.heroicLevel) return 'heroic';
  if (level >= d.min && level <= d.max) return 'green';
  if (level >= d.min - slack && level <= d.max + slack) return 'yellow';
  return 'out';
}

/** All dungeons that the anchor can run (green or yellow or heroic). */
export function eligibleDungeons(level: number, slack = DEFAULT_SLACK): Array<{ dungeon: Dungeon; status: Eligibility }> {
  return ALL_DUNGEONS
    .map((dungeon) => ({ dungeon, status: dungeonStatus(level, dungeon, slack) }))
    .filter((x) => x.status !== 'out');
}

/**
 * For a given dungeon, return all candidate characters (excluding the
 * anchor itself) whose level lands in green or yellow against `d`.
 * Sorted: same-account first, then by status (green before yellow),
 * then by level desc.
 */
export function eligibleGroupmates(
  anchorKey: string,
  anchorOwner: string | null | undefined,
  candidates: CharacterSummary[],
  d: Dungeon,
  slack = DEFAULT_SLACK
): Array<{ char: CharacterSummary; status: Eligibility; sameAccount: boolean }> {
  const out: Array<{ char: CharacterSummary; status: Eligibility; sameAccount: boolean }> = [];
  for (const c of candidates) {
    if (c.key === anchorKey) continue;
    const status = dungeonStatus(c.level, d, slack);
    if (status === 'out') continue;
    out.push({
      char: c,
      status,
      sameAccount: !!anchorOwner && c.owner === anchorOwner,
    });
  }
  out.sort((a, b) => {
    if (a.sameAccount !== b.sameAccount) return a.sameAccount ? -1 : 1;
    const rank: Record<Eligibility, number> = { heroic: 0, green: 1, yellow: 2, out: 3 };
    if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
    return b.char.level - a.char.level;
  });
  return out;
}

export function statusLabel(s: Eligibility): string {
  switch (s) {
    case 'green':  return 'In range';
    case 'yellow': return 'Tight';
    case 'heroic': return 'Heroic';
    case 'out':    return '—';
  }
}

/**
 * Group eligible-dungeon results by expansion. Within each expansion they
 * keep their incoming order, so callers should pre-sort if a particular
 * order matters (e.g. by status priority).
 */
export function groupByExpansion(
  rows: Array<{ dungeon: Dungeon; status: Eligibility }>
): Array<{ expansion: Expansion; rows: typeof rows }> {
  const map = new Map<Expansion, typeof rows>();
  for (const r of rows) {
    const arr = map.get(r.dungeon.expansion);
    if (arr) arr.push(r);
    else map.set(r.dungeon.expansion, [r]);
  }
  // Order: wotlk first (most relevant for 80s), then tbc, then vanilla.
  const order: Expansion[] = ['wotlk', 'tbc', 'vanilla'];
  return order
    .filter((e) => map.has(e))
    .map((expansion) => ({ expansion, rows: map.get(expansion)! }));
}
